/**
 * The text field recipe. Every text-like control (input, textarea, select trigger, combobox trigger, search, tag
 * input, affix input) is drawn from these class strings, combined with `cn()` so callers can override single
 * properties such as width or padding.
 *
 * States: the border is `control-border` (at least 3:1 on `field` and `surface`) and darkens on hover unless the
 * field has focus; focus draws the ring color as border and a 2px outline (`field-ring`); `aria-invalid` uses a 2px
 * danger border; a `readonly` field keeps its `control-border` boundary and full-contrast text on a sunken fill; a
 * disabled field dims its text and border and shows a not-allowed cursor.
 */

/**
 * Height of a single-line field: `--field-height` from index.css, 36px below the sm breakpoint and 32px from sm up.
 * A single height class, so a caller's `h-*` replaces it at every width.
 */
export const fieldHeight = 'h-(--field-height)';

/** Classes for an element that is itself the field and takes focus (input, textarea, select trigger). */
export const fieldClasses =
  'field-ring w-full min-w-0 rounded-sm border border-control-border bg-field px-2.5 text-sm text-text transition-[border-color,background-color] duration-150 placeholder:text-subtle hover:not-focus-visible:border-subtle aria-invalid:border-danger aria-invalid:ring-1 aria-invalid:ring-inset aria-invalid:ring-danger aria-invalid:hover:border-danger [&[readonly]:not(:disabled)]:bg-sunken disabled:cursor-not-allowed disabled:border-border disabled:bg-sunken disabled:text-subtle';

/**
 * Classes for a wrapper drawn as the field while a descendant input takes focus (affixes, tag inputs, search fields
 * with icons). The descendant uses `fieldInputClasses`; invalid, read-only and disabled states follow it.
 */
export const fieldFrameClasses =
  'field-ring flex w-full min-w-0 items-center rounded-sm border border-control-border bg-field text-sm text-text transition-[border-color,background-color] duration-150 hover:border-subtle has-[[aria-invalid=true]]:border-danger has-[[aria-invalid=true]]:ring-1 has-[[aria-invalid=true]]:ring-inset has-[[aria-invalid=true]]:ring-danger has-[[aria-invalid=true]]:hover:border-danger has-[input[readonly]:not(:disabled)]:bg-sunken has-[:disabled]:cursor-not-allowed has-[:disabled]:border-border has-[:disabled]:bg-sunken has-[:disabled]:text-subtle';

/** Classes for the bare input inside a `fieldFrameClasses` wrapper. */
export const fieldInputClasses =
  'h-full w-full min-w-0 bg-transparent px-2.5 text-text outline-none placeholder:text-subtle disabled:cursor-not-allowed disabled:text-subtle';
